/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  CommonFilterProperties,
  // CommonFilterProperties,
  FilterUpdateProperties,
} from '../../interface/filter.interface';
import {
  FetchStationProperties,
  RequestStationInterface,
  StationFilter,
  StationInitialProperties,
} from '../../interface/station-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import station from '../../utils/api/station';

// a thunk to fetch stations
export const fetchPendingStations = createAsyncThunk<
  FetchStationProperties,
  RequestStationInterface
>(
  'pending-station-management/fetchPendingStations',
  async (data: RequestStationInterface, { dispatch }) => {
    try {
      const response = await station.fetchStations(
        removeEmptyKeys({ ...data, submissionStatus: 'pending' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'pending-station-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await station.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: StationInitialProperties = {
  stations: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  allStations: [],
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const pendingStationSlice = createSlice({
  name: 'pending-station-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    setPendingStationFilterData: (
      state: StationInitialProperties,
      action: PayloadAction<StationFilter>,
    ) => {
      state.filter = action.payload;
    },

    /**
     *Update approved station management
     * @param state
     * @param action
     */
    updateStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetPendingStationToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function for set sortBy
     * @param state 
     * @param action 
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingStations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingStations.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.stations = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          amenities: stationData?.amenities || [],
          opensAt: ConvertUTCtoHHMM(stationData?.opensAt),
          closesAt: ConvertUTCtoHHMM(stationData?.closesAt),
        }));
      })
      .addCase(fetchPendingStations.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setPendingStationFilterData,
  resetPendingStationToInitialState,
  updateStationFilters,
  setSortData
} = pendingStationSlice.actions;

// Export the reducer
export default pendingStationSlice.reducer;
